import { calendarModelsQuery, useUpdateCalendarMutation } from "@/api/supplier";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { AvailableTimeTransformer } from "@/pages/supplier/settings/calendars/AvailableTimeTransformer";
import { AvailableTimesEditor } from "@/pages/supplier/settings/calendars/AvailableTimesEditor";
import { BookingControlsEditor } from "@/pages/supplier/settings/calendars/BookingControlsEditor";
import { titleCase } from "@common/utils/string";
import { ErrorMessage, Field, Flex, Label, QueryHandler, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { UpdateSupplierCalendarBody } from "@givenwell/management-api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AvailableTimeDayModel, CalendarModel, SupplierCalendarSettingsModel } from "../model";

export const Route = createFileRoute("/supplier/$supplierId/settings/calendars/$calendarId/")({
  component: EditCalendarSettingsLoader,
});

function EditCalendarSettingsLoader() {
  const { supplierId, calendarId } = Route.useParams();
  const query = useQuery(calendarModelsQuery(supplierId));

  return (
    <PageWrapper title="Edit Calendar" maxWidth={1400}>
      <QueryHandler
        query={query}
        success={({ data: calendars }) => {
          const calendar = calendars.find(cal => cal.id === calendarId);
          return calendar ? (
            <EditCalendarSettings initialCalendar={calendar} />
          ) : (
            <Navigate to="/supplier/$supplierId/settings/calendars" params={{ supplierId }} />
          );
        }}
      />
    </PageWrapper>
  );
}

type EditCalendarSettingsProps = { initialCalendar: CalendarModel };
function EditCalendarSettings({ initialCalendar }: EditCalendarSettingsProps) {
  const { supplierId } = Route.useParams();
  const updateMut = useUpdateCalendarMutation();
  const navigate = useNavigate();
  const [availableTimesMessage, setAvailableTimesMessage] = useState<string | undefined>(undefined);

  const [model, setModel] = useState<CalendarModel>({ ...initialCalendar });

  async function handleInput<F extends keyof CalendarModel>(field: F, value: CalendarModel[F]) {
    setModel({ ...model, [field]: value });

    if (availableTimesMessage) {
      setAvailableTimesMessage("");
    }
  }

  function handleControlsInput<F extends keyof SupplierCalendarSettingsModel>(
    field: F,
    value: SupplierCalendarSettingsModel[F],
  ) {
    handleInput("settings", { ...model.settings, [field]: value });
  }

  function handleAvailableTimesChange(days: AvailableTimeDayModel[]) {
    setModel({ ...model, settings: { ...model.settings, availableDays: days } });
  }

  async function handleSaveChangesClick() {
    if (supplierId === undefined) {
      return;
    }

    for (const day of model.settings.availableDays) {
      if (AvailableTimeTransformer.hasOverlappingRanges(day)) {
        setAvailableTimesMessage(`${titleCase(day.day)} has overlapping times.`);
        return;
      }
    }

    const request: UpdateSupplierCalendarBody = {
      resource_name: model.resourceName,
      booking_configuration: {
        available_times: AvailableTimeTransformer.toLocalDayTimeRanges(model.settings.availableDays),
        timezone: model.settings.timezone,
        slotting_method: model.settings.slottingMethod,
        interval_minutes: model.settings.intervalMinutes,
        advance_booking_days: model.settings.advanceBookingDays,
        minimum_notice_time_minutes: model.settings.minimumNoticeTimeHours * 60,
      },
    };
    await updateMut.mutateAsync({ supplierId, calendarId: initialCalendar.id, request });
    toast.success("Calendar updated");
    navigate({
      to: "/supplier/$supplierId/settings/calendars",
      params: {
        supplierId,
      },
    });
  }

  return (
    <Flex css={{ gap: 6, flexDir: "row" }}>
      <Card css={{ flex: 1, maxW: 600 }}>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSaveChangesClick();
          }}
        >
          <Flex css={{ flex: "1 0 0px", flexDirection: "column", gap: 24 }}>
            <Field required>
              <Label>Calendar/Resource Name</Label>
              <Input value={model.resourceName} onValueChange={v => handleInput("resourceName", v)} />
            </Field>
            <Field>
              <Label css={{ borderBottom: `1px solid lightgray` }}>Availability</Label>
              <AvailableTimesEditor
                isLoading={false}
                days={model.settings.availableDays}
                onChange={days => handleAvailableTimesChange(days)}
              />
              {availableTimesMessage && <ErrorMessage>{availableTimesMessage}</ErrorMessage>}
            </Field>
            <Field>
              <Label>Settings</Label>
              <BookingControlsEditor model={model.settings} isLoading={false} onInput={handleControlsInput} />
            </Field>
            <Button
              variant="tonal"
              color="primary"
              type="submit"
              disabled={updateMut.isPending}
              css={{ alignSelf: "flex-start" }}
            >
              Save changes
            </Button>
          </Flex>
        </form>
      </Card>
    </Flex>
  );
}
